import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { authHeaders, resolveUrl, type HuntChatMessage } from '../api';

/**
 * I2: jaktens lyssning i telefonen. I iOS-appen tolkar Apples taligenkänning talet i telefonen
 * (QHuntListenPlugin i ios/App/App/QVoicePlugin.swift) i stället för att 15 s-bitar skickas till Groq.
 * Resultatet går samma väg som förut i KartaTab: transkriptionen, Q-fliken (hunt_q_heard), walkie-anrop
 * och sammanfattningen. Saknas lokal igenkänning (webbläsare, Android, svenska inte nedladdad, nekad
 * behörighet, mikrofonen upptagen) används Groq som förut.
 *
 * Mikrofonen har en ägare åt gången (regel 1 i QVoicePlugin.swift): innan WebKit tar mikrofonen (LiveKit-radion,
 * videoinspelning, Q, ChatGPT-rösten) stoppas den här lyssningen, och den startas igen efteråt.
 *
 * Här finns också röst- och videomeddelanden till jaktchatten (POST /api/q/hunt/:meetingId/chat-media).
 */

export type NativeHeard = { id: string; text: string; ts: number; durationMs: number };
type Base64Audio = { audio: string; mime: string };

interface QHuntListenApi {
  isAvailable(o: { lang: string }): Promise<{ onDevice: boolean; running: boolean }>;
  start(o: { lang: string }): Promise<{ onDevice: boolean }>;
  stop(): Promise<void>;
  segmentAudio(o: { id: string }): Promise<Base64Audio>;
  recordStart(): Promise<void>;
  recordStop(o: { cancel?: boolean }): Promise<Base64Audio & { durationMs: number; text: string; cancelled?: boolean }>;
  addListener(event: 'heard', cb: (e: NativeHeard) => void): Promise<PluginListenerHandle>;
  addListener(event: 'partial', cb: (e: { text: string }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'shot', cb: (e: { ts: number }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'state', cb: (e: { active: boolean; reason?: string }) => void): Promise<PluginListenerHandle>;
}

const Plugin = registerPlugin<QHuntListenApi>('QHuntListen');

export function nativeHuntListenSupported(): boolean {
  return Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('QHuntListen');
}

export function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export interface NativeHuntListen {
  stop(): Promise<void>;
  /** Ljudet för en hörd mening (walkie-talkie). null om det inte finns kvar. */
  audio(id: string): Promise<Blob | null>;
}

/**
 * Startar lyssningen i telefonen. null när det inte går (då används Groq); felet loggas.
 * onLost: telefonen slutade lyssna av sig själv (t.ex. WebKit tog mikrofonen) – byt till Groq.
 */
export async function startNativeHuntListen(lang: 'sv' | 'en', h: {
  onHeard: (e: NativeHeard) => void;
  onPartial: (text: string) => void;
  onShot: () => void;
  onLost: (reason: string) => void;
}): Promise<NativeHuntListen | null> {
  if (!nativeHuntListenSupported()) return null;
  const handles: PluginListenerHandle[] = [];
  const removeAll = () => { for (const l of handles.splice(0)) void l.remove(); };
  let stopped = false;
  try {
    handles.push(
      await Plugin.addListener('heard', e => { if (!stopped) h.onHeard(e); }),
      await Plugin.addListener('partial', e => { if (!stopped) h.onPartial(e.text); }),
      await Plugin.addListener('shot', () => { if (!stopped) h.onShot(); }),
      await Plugin.addListener('state', e => {
        if (stopped || e.active || e.reason === 'stopped') return;
        stopped = true;
        removeAll();
        h.onLost(e.reason || 'unknown');
      }),
    );
    await Plugin.start({ lang: lang === 'en' ? 'en-US' : 'sv-SE' });
  } catch (err) {
    console.warn('[QVENARI-TRANSCRIPT] Lokal taligenkänning saknas – använder Groq:', (err as { code?: string })?.code || err);
    removeAll();
    return null;
  }
  return {
    stop: async () => {
      if (stopped) return;
      stopped = true;
      // "heard" för en mening som pågår kommer efter stop: lyssnarna tas bort först när den hunnit komma.
      await Plugin.stop().catch(() => {});
      setTimeout(removeAll, 6000);
    },
    audio: async id => {
      try {
        const r = await Plugin.segmentAudio({ id });
        return base64ToBlob(r.audio, r.mime);
      } catch {
        return null;
      }
    },
  };
}

// ── Röstmeddelanden till jaktchatten ──

export type RecordedVoice = { blob: Blob; durationMs: number; text: string };
export interface VoiceRecording {
  /** Avslutar och ger ljudet; null om inget spelades in. */
  stop(): Promise<RecordedVoice | null>;
  cancel(): void;
}

/** Längsta röstmeddelande (samma gräns som i telefonen). */
export const VOICE_MESSAGE_MAX_MS = 120_000;
/** Kortare tryck än så räknas inte som röstmeddelande. */
export const VOICE_MESSAGE_MIN_MS = 600;

/** Med telefonens lyssning igång: samma ljudmotor spelar in (ingen ny ägare av mikrofonen). */
export async function startNativeVoiceRecording(): Promise<VoiceRecording | null> {
  if (!nativeHuntListenSupported()) return null;
  try {
    await Plugin.recordStart();
  } catch {
    return null;
  }
  let done = false;
  return {
    stop: async () => {
      if (done) return null;
      done = true;
      try {
        const r = await Plugin.recordStop({});
        if (!r.audio) return null;
        return { blob: base64ToBlob(r.audio, r.mime), durationMs: r.durationMs, text: (r.text || '').trim() };
      } catch {
        return null;
      }
    },
    cancel: () => { if (!done) { done = true; void Plugin.recordStop({ cancel: true }).catch(() => {}); } },
  };
}

export function pickRecorderMime(kind: 'audio' | 'video'): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const order = kind === 'audio'
    ? ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']
    : ['video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return order.find(m => MediaRecorder.isTypeSupported(m)) ?? '';
}

/** Webbens röstmeddelande (getUserMedia + MediaRecorder). Jaktlyssningen ska vara pausad först. */
export async function startWebVoiceRecording(): Promise<VoiceRecording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = pickRecorderMime('audio');
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 48_000 } : {});
  const chunks: Blob[] = [];
  rec.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
  const started = Date.now();
  rec.start();
  const release = () => stream.getTracks().forEach(t => t.stop());
  const limit = setTimeout(() => { if (rec.state === 'recording') rec.stop(); }, VOICE_MESSAGE_MAX_MS);
  const stopped = new Promise<void>(resolve => { rec.onstop = () => resolve(); });
  let cancelled = false;
  return {
    stop: async () => {
      clearTimeout(limit);
      if (rec.state === 'recording') rec.stop();
      await stopped;
      release();
      if (cancelled || !chunks.length) return null;
      return { blob: new Blob(chunks, { type: rec.mimeType || mime || 'audio/webm' }), durationMs: Date.now() - started, text: '' };
    },
    cancel: () => {
      cancelled = true;
      clearTimeout(limit);
      if (rec.state === 'recording') rec.stop();
      release();
    },
  };
}

/** Röst- eller videomeddelande till jaktchatten. Svaret är meddelandet som GraphQL ger det. */
export async function sendHuntChatMedia(meetingId: string, m: {
  kind: 'audio' | 'video'; clientId: string; blob: Blob; durationMs?: number; text?: string; lat?: number; lng?: number;
}): Promise<HuntChatMessage> {
  const fd = new FormData();
  fd.append('kind', m.kind);
  fd.append('clientId', m.clientId);
  if (m.text) fd.append('text', m.text);
  if (m.durationMs != null) fd.append('durationMs', String(Math.round(m.durationMs)));
  if (m.lat != null && m.lng != null) { fd.append('lat', String(m.lat)); fd.append('lng', String(m.lng)); }
  const type = m.blob.type.split(';')[0];
  const ext = m.kind === 'video' ? (type.includes('webm') ? 'webm' : type.includes('quicktime') ? 'mov' : 'mp4') : (type.includes('webm') ? 'webm' : 'm4a');
  fd.append('file', m.blob, `${m.kind}.${ext}`);
  const res = await fetch(resolveUrl(`/api/q/hunt/${encodeURIComponent(meetingId)}/chat-media`), { method: 'POST', headers: authHeaders(), body: fd });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
  return body as HuntChatMessage;
}
